import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import LazyImage from "./LazyImage";
import Toast from "./Toast";
import { PiHeartFill } from "react-icons/pi";

// A single tap opens the lightbox, but that can't fire immediately — it has
// to wait this long to see whether a second tap follows (double-tap = like,
// Instagram-style) instead. Keep this short so the lightbox still feels
// responsive to a genuine single tap.
const DOUBLE_TAP_DELAY = 280;

// Every photo in a post is shown in its own shape, uncropped. The frame is as tall
// as the photo on show, and while swiping from one photo to the next its height
// glides from the one to the other — so there are no empty bands above or below a
// shorter photo, and nothing is cut off a differently shaped one. A photo's shape
// is read from the photo itself once it has loaded (never by fetching it early —
// the later photos of a post stay lazy, downloaded only as you swipe towards them),
// and remembered per address so scrolling the feed doesn't make a post jump.
const DEFAULT_RATIO = 4 / 5; // until the first photo has loaded
const knownRatios = new Map();

function usePhotoRatios(photos) {
  const key = photos.join("|");
  const [ratios, setRatios] = useState(() => photos.map(url => knownRatios.get(url) || null));

  useEffect(() => {
    setRatios(photos.map(url => knownRatios.get(url) || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Called from a photo's own onLoad.
  const report = useCallback((i, url, img) => {
    if (!img.naturalWidth || !img.naturalHeight) return;
    const ratio = img.naturalWidth / img.naturalHeight;
    knownRatios.set(url, ratio);
    setRatios(prev => {
      if (prev[i] === ratio) return prev;
      const next = prev.slice();
      next[i] = ratio;
      return next;
    });
  }, []);

  return [ratios, report];
}

export default function ImageCarousel({ images, imgStyle, alt, onImageClick, onDoubleTap }) {
  const photos = images && images.length > 0 ? images : [];
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [showHeart, setShowHeart] = useState(false);
  const [heartBurstKey, setHeartBurstKey] = useState(0);
  const [likeErrorToast, setLikeErrorToast] = useState("");
  const tapState = useRef({ lastTap: 0, timer: null });
  const heartHideTimer = useRef(null);
  const [ratios, reportRatio] = usePhotoRatios(photos.length > 1 ? photos : []);
  const ratiosRef = useRef(ratios);
  ratiosRef.current = ratios;

  // Sets the track's height from where it is scrolled to: exactly the height of
  // the photo on show, or in between two photos' heights while sliding.
  function fitHeight() {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;

    const known = ratiosRef.current;
    const fallback = known[0] || DEFAULT_RATIO;
    const heightOf = i => 1 / (known[i] || fallback); // relative to the width

    const position = el.scrollLeft / el.clientWidth;
    const from = Math.max(0, Math.min(photos.length - 1, Math.floor(position)));
    const to = Math.min(photos.length - 1, from + 1);
    const t = Math.max(0, Math.min(1, position - from));
    el.style.height = `${(heightOf(from) + (heightOf(to) - heightOf(from)) * t) * el.clientWidth}px`;
  }

  // Before the first paint, and again whenever a photo's shape becomes known.
  useLayoutEffect(() => {
    fitHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratios]);

  // The card's width changing (rotating the phone, resizing) changes the height too.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    // Changing the height inside the observer's own callback makes the browser
    // report "ResizeObserver loop completed with undelivered notifications", so
    // the change waits for the next frame.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fitHeight);
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);

  // Without this, a single tap that's still waiting out the double-tap
  // window when this card scrolls out and unmounts (fast feed scrolling)
  // fires onImageClick moments later against a card the user isn't even
  // looking at anymore, popping the lightbox open unexpectedly.
  useEffect(() => {
    const state = tapState.current;
    return () => {
      if (state.timer) clearTimeout(state.timer);
      if (heartHideTimer.current) clearTimeout(heartHideTimer.current);
    };
  }, []);

  if (photos.length === 0) return null;

  function handleTap(i) {
    const now = Date.now();
    const { lastTap, timer } = tapState.current;

    if (now - lastTap < DOUBLE_TAP_DELAY) {
      clearTimeout(timer);
      tapState.current.lastTap = 0;
      tapState.current.timer = null;

      // The key bump forces a fresh <span> so the CSS animation restarts on
      // every double-tap — without it, a second double-tap while the burst
      // from the first is still fading just flips the same boolean back to
      // true on the same DOM node, so the browser never replays the
      // keyframes and the tap gets no visual feedback at all.
      setShowHeart(true);
      setHeartBurstKey(k => k + 1);
      if (heartHideTimer.current) clearTimeout(heartHideTimer.current);
      heartHideTimer.current = setTimeout(() => setShowHeart(false), 700);

      // The heart burst above plays immediately (optimistic — a double-tap
      // has to feel instant), but onDoubleTap's like RPC can still fail
      // (network issue, RLS, etc.) after the fact. Without checking its
      // result, a failed like still showed the same "liked!" animation as
      // a successful one, telling the user something happened that didn't.
      const result = onDoubleTap?.();
      if (result?.then) {
        result.then(res => {
          if (!res?.error) return;
          // likeDesign (utils/likes.js) returns this specific error for a
          // logged-out visitor — "try again" would be wrong there, since
          // retrying does nothing until they actually log in.
          setLikeErrorToast(
            res.error.message === "not_logged_in"
              ? "Please log in to like"
              : "Couldn't like this. Try again."
          );
        });
      }
    } else {
      tapState.current.lastTap = now;
      tapState.current.timer = setTimeout(() => {
        onImageClick?.({ images: photos, index: i });
      }, DOUBLE_TAP_DELAY);
    }
  }

  function handleScroll() {
    fitHeight();
    const el = trackRef.current;
    // clientWidth is 0 while the card is mid-transition or not yet laid
    // out — dividing by it gives NaN, which then fails every `i === index`
    // dot/counter comparison and leaves them all showing wrong.
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setIndex(Math.max(0, Math.min(photos.length - 1, i)));
  }

  return (
    <div style={wrapper}>
      <style>{`
        @keyframes virsa-heart-burst {
          0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          25% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
          45% { transform: translate(-50%, -50%) scale(0.95); opacity: 1; }
          75% { transform: translate(-50%, -50%) scale(1.05); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
        }
      `}</style>

      {photos.length === 1 ? (
        <LazyImage src={photos[0]} alt={alt} style={imgStyle} onClick={() => handleTap(0)} />
      ) : (
        <div
          ref={trackRef}
          style={track}
          onScroll={handleScroll}
          className="virsa-carousel-track"
        >
          <style>{`
            .virsa-carousel-track::-webkit-scrollbar { display: none; }
          `}</style>
          {photos.map((url, i) => (
            <LazyImage
              key={i}
              src={url}
              alt={alt}
              eager={i === 0}
              style={{
                ...imgStyle,
                scrollSnapAlign: "start",
                flex: "0 0 100%",
                width: "100%",
                height: "auto",
                alignSelf: "flex-start"
              }}
              onClick={() => handleTap(i)}
              onLoad={e => reportRatio(i, url, e.currentTarget)}
            />
          ))}
        </div>
      )}

      {photos.length > 1 && (
        <>
          <span style={counterBadge}>
            {index + 1}/{photos.length}
          </span>

          <div style={dotsRow}>
            {photos.map((_, i) => (
              <span key={i} style={i === index ? dotActive : dot} />
            ))}
          </div>
        </>
      )}

      {showHeart && (
        <span key={heartBurstKey} style={heartBurst}>
          <PiHeartFill size={90} />
        </span>
      )}

      {likeErrorToast && <Toast text={likeErrorToast} onClose={() => setLikeErrorToast("")} />}
    </div>
  );
}

const wrapper = { position: "relative" };

const track = {
  display: "flex",
  // The height is set from the photo on show (see fitHeight); a taller photo's
  // lower part is out of sight until it is the one on show.
  alignItems: "flex-start",
  background: "var(--border-soft)",
  overflowX: "auto",
  overflowY: "hidden",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
};

const counterBadge = {
  position: "absolute",
  top: 10,
  right: 10,
  background: "rgba(20, 14, 8, 0.65)",
  color: "var(--accent-contrast)",
  padding: "2px 9px",
  borderRadius: 12,
  fontSize: 11.5,
  fontWeight: 700,
};

const dotsRow = {
  position: "absolute",
  bottom: 10,
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  gap: 5,
};

const dot = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.55)",
};

const dotActive = {
  ...dot,
  background: "var(--bg-surface)",
  width: 7,
  height: 7,
};

const heartBurst = {
  position: "absolute",
  top: "50%",
  left: "50%",
  color: "#fff",
  filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.45))",
  pointerEvents: "none",
  animation: "virsa-heart-burst 0.7s ease-out",
};
